/**MTNEsgDisclosure.js — ESG, IFRS S2 & Disclosure Center */
import React, { useState } from "react";
import { useMTN } from "@/context/MTNContext";
import { Download, Loader, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ExecutiveActionPanel from "@/components/ExecutiveActionPanel";

const TABS=[{id:"ifrs",label:"IFRS S1/S2"},{id:"tcfd",label:"TCFD"},{id:"gri",label:"GRI/SASB"},{id:"ngx",label:"NGX"},{id:"reports",label:"Reports"}];

export default function MTNEsgDisclosure() {
  const { group, loading, selectedPeriod } = useMTN();
  const [activeTab,setActiveTab]=useState("ifrs");
  const [downloading,setDownloading]=useState(null);

  const downloadPdf=async(type)=>{
    setDownloading(type);
    try{
      const r=await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mtn/pdf/${type}/${selectedPeriod}`,
        {headers:{Authorization:`Bearer ${localStorage.getItem("decarboniq_token")}`}});
      if(!r.ok)throw new Error();
      const blob=await r.blob();
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");a.href=url;a.download=`MTN_${type}_${selectedPeriod}.pdf`;a.click();
      URL.revokeObjectURL(url);toast.success("Downloaded");
    }catch{toast.error("Download failed");}
    finally{setDownloading(null);}
  };

  if(loading)return<div style={{padding:40,color:"#64748b",fontSize:12}}>Loading…</div>;
  const conf=group.data_confidence_avg||45;
  const readiness=Math.round(Math.min(85,conf*0.70+10));

  return(
    <div style={{padding:"24px 28px"}}>
      <div style={{marginBottom:20}}>
        <span style={{fontSize:9,fontWeight:800,color:"#FFCB00",letterSpacing:".08em",textTransform:"uppercase",background:"#FFCB0015",border:"1px solid #FFCB0030",padding:"2px 8px",borderRadius:4}}>MTN Nigeria</span>
        <h1 style={{fontSize:20,fontWeight:900,color:"#f1f5f9",marginTop:6}}>ESG & IFRS S2 Disclosure Center</h1>
        <p style={{fontSize:11,color:"#475569"}}>IFRS S1/S2 · TCFD · GRI · SASB Telecom · NGX ESG · Assurance readiness</p>
      </div>

      {/* Readiness summary */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginBottom:16}}>
        {[["IFRS S2 Readiness",`${readiness}%`,readiness>=60?"#22c55e":"#f59e0b"],
          ["TCFD Readiness","50%","#f59e0b"],
          ["GRI Alignment","Partial","#60a5fa"],
          ["NGX ESG","In Progress","#f59e0b"],
          ["Assurance Ready","No","#ef4444"],
        ].map(([l,v,c])=>(
          <div key={l} style={{background:"#0d1423",border:`1px solid ${c}25`,borderRadius:8,padding:"12px 14px"}} data-testid={`esg-metric-${l.toLowerCase().replace(/\s+/g,"-")}`}>
            <p style={{fontSize:8,color:"#475569",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{l}</p>
            <p style={{fontSize:16,fontWeight:900,color:c}}>{v}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:4,background:"#0a0f1e",border:"1px solid #1e293b",borderRadius:8,padding:6,marginBottom:16}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setActiveTab(t.id)}
            style={{flex:1,padding:"7px 8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:10,fontWeight:600,
              background:activeTab===t.id?"#FFCB00":"transparent",color:activeTab===t.id?"#000":"#475569"}}
            data-testid={`esg-tab-${t.id}`}>{t.label}
          </button>
        ))}
      </div>

      {/* IFRS S1/S2 */}
      {activeTab==="ifrs" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} data-testid="ifrs-section">
          <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
            <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>IFRS S2 Pillar Assessment</p>
            {[["Governance",60,"Board ESG committee active. TCFD-aligned governance requires formalisation. Climate risk not yet in risk register."],
              ["Strategy",45,"Transition risks identified at high level. Physical risk (NGFS scenarios) not yet commissioned."],
              ["Risk Management",50,"Climate risk framework in development. ERM integration incomplete. Monitoring protocols needed."],
              ["Metrics & Targets",conf.toFixed(0),"GHG inventory established via SSOT. SBTi submission pending. Intensity targets not formalised."],
            ].map(([lbl,score,note])=>(
              <div key={lbl} style={{marginBottom:12,borderBottom:"1px solid #0f172a",paddingBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                  <span style={{fontSize:10,fontWeight:700,color:"#f1f5f9"}}>{lbl}</span>
                  <span style={{fontSize:11,fontWeight:900,color:score>=65?"#22c55e":score>=45?"#f59e0b":"#ef4444"}}>{score}/100</span>
                </div>
                <div style={{height:4,background:"#1e293b",borderRadius:2,overflow:"hidden",marginBottom:6}}><div style={{height:"100%",width:`${score}%`,background:score>=65?"#22c55e":score>=45?"#f59e0b":"#ef4444",borderRadius:2}}/></div>
                <p style={{fontSize:9,color:"#64748b"}}>{note}</p>
              </div>
            ))}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}}>
              <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>Disclosure Gap Analysis — What Blocks External Assurance?</p>
              {[["Physical risk scenario analysis (NGFS)","Critical","Not commissioned — blocks IFRS S2 Strategy pillar"],
                ["Transition risk register","Critical","Draft only — not integrated into ERM"],
                ["Climate scenario quantification","Critical","Qualitative only — IFRS S2 requires financial quantification"],
                ["SBTi submission","High","Pending — increasingly required by institutional investors"],
                ["Board climate mandate","High","Informal — requires board-approved climate charter"],
                ["GHG independent assurance","High","No third-party assurance — blocks external disclosure credibility"],
                ["Supply chain (Scope 3 Cat.1) audit","Medium","Vendor declarations only — no independent verification"],
                ["TCFD formal report","Medium","TCFD content exists in SSOT — formal publication required"],
              ].map(([gap,sev,note])=>(
                <div key={gap} style={{marginBottom:6,borderBottom:"1px solid #0f172a",paddingBottom:5}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:1}}>
                    <span style={{fontSize:9,fontWeight:600,color:"#f1f5f9"}}>{gap}</span>
                    <span style={{fontSize:7,fontWeight:700,padding:"1px 5px",borderRadius:3,background:sev==="Critical"?"#7f1d1d30":sev==="High"?"#78350f30":"#1e293b",color:sev==="Critical"?"#ef4444":sev==="High"?"#f59e0b":"#64748b"}}>{sev}</span>
                  </div>
                  <p style={{fontSize:8,color:"#475569"}}>{note}</p>
                </div>
              ))}
            </div>
            <ExecutiveActionPanel compact={true} title="Regulatory Actions" actions={[
              {severity:"Critical",category:"IFRS S2",title:"Commission NGFS climate scenario analysis — FY2026 deadline",description:"IFRS S2 Strategy pillar requires physical + transition risk quantification under NGFS scenarios.",actionRequired:"Issue RFP for climate risk consultancy immediately",financialExposure:"Regulatory non-compliance + investor access",timeline:"Q2 2026",owner:"CFO + Board Risk Committee"},
            ]} />
          </div>
        </div>
      )}

      {/* TCFD */}
      {activeTab==="tcfd" && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}} data-testid="tcfd-section">
          {[["Governance","Formalise Board climate risk mandate and TCFD reporting charter",60,["Board ESG committee active","Annual climate risk review established","No formal TCFD charter"]],
            ["Strategy","Commission NGFS scenario analysis (1.5°C + 3°C) for network infrastructure",45,["Transition risk: HFO/diesel cost exposure identified","Physical risk: grid stability not formally assessed","Scenario analysis not yet commissioned"]],
            ["Risk Management","Integrate climate risk into enterprise risk management framework",50,["Climate risk framework in development","ERM integration pending","Climate risk committee not yet established"]],
            ["Metrics & Targets","Submit SBTi targets and formalise carbon intensity KPIs",conf.toFixed(0),["GHG inventory complete","SBTi submission Q3 2026","Carbon intensity target: -35% Scope 1+2 by 2030"]],
          ].map(([pillar,action,score,items])=>(
            <div key={pillar} style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:14}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <p style={{fontSize:10,fontWeight:700,color:"#f1f5f9"}}>TCFD: {pillar}</p>
                <span style={{fontSize:13,fontWeight:900,color:score>=65?"#22c55e":score>=45?"#f59e0b":"#ef4444"}}>{score}/100</span>
              </div>
              <div style={{height:4,background:"#1e293b",borderRadius:2,overflow:"hidden",marginBottom:8}}><div style={{height:"100%",width:`${score}%`,background:score>=65?"#22c55e":score>=45?"#f59e0b":"#ef4444",borderRadius:2}}/></div>
              {items.map((item,i)=>(
                <div key={i} style={{display:"flex",gap:5,marginBottom:3}}>
                  <CheckCircle2 size={9} color={i===0?"#22c55e":"#f59e0b"} style={{flexShrink:0,marginTop:1}}/>
                  <p style={{fontSize:9,color:"#94a3b8"}}>{item}</p>
                </div>
              ))}
              <div style={{background:"#FFCB0015",borderRadius:5,padding:8,marginTop:8}}>
                <p style={{fontSize:8,fontWeight:700,color:"#FFCB00",marginBottom:1}}>Priority Action</p>
                <p style={{fontSize:9,color:"#f1f5f9"}}>{action}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* GRI/SASB */}
      {activeTab==="gri" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="gri-section">
          <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>SASB TC-TL Telecom Sector Metrics</p>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr style={{borderBottom:"1px solid #1e293b"}}>
              {["SASB Metric","Category","Status","MTN Nigeria Status"].map(h=>(
                <th key={h} style={{textAlign:"left",padding:"6px 10px",fontSize:8,color:"#475569",fontWeight:600}}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {[["Energy consumption (kWh/yr)","TC-TL-130a.1","Required","DeCarbonIQ SSOT tracking active"],
                ["% renewable / grid","TC-TL-130a.1","Required","6.2% renewable — target 30% by 2030"],
                ["Data security incidents","TC-TL-230a.1","Required","Separate cybersecurity disclosure"],
                ["E-waste (tonnes/yr)","TC-TL-440a.1","Required","E-waste programme active, annual tracking"],
                ["% devices recycled","TC-TL-440a.1","Required","SIM exchange programme active"],
                ["Network reliability","TC-TL-550a.1","Recommended","NCC regulatory reporting active"],
              ].map(([m,cat,req,status])=>(
                <tr key={m} style={{borderBottom:"1px solid #0f172a"}}>
                  <td style={{padding:"7px 10px",fontSize:9,fontWeight:600,color:"#f1f5f9"}}>{m}</td>
                  <td style={{padding:"7px 10px",fontSize:8,color:"#94a3b8"}}>{cat}</td>
                  <td style={{padding:"7px 10px"}}><span style={{fontSize:8,fontWeight:700,padding:"2px 5px",borderRadius:3,background:req==="Required"?"#14532d20":"#1e293b",color:req==="Required"?"#22c55e":"#64748b"}}>{req}</span></td>
                  <td style={{padding:"7px 10px",fontSize:9,color:"#60a5fa"}}>{status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* NGX */}
      {activeTab==="ngx" && (
        <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="ngx-section">
          <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>NGX ESG Disclosure Guidelines — FY 2025/2026</p>
          {[["Climate Strategy & Risk","Partial","Climate strategy in development. Scenario analysis pending. Board climate mandate in progress.","50"],
            ["GHG Emissions Reporting","Active","Scope 1+2+3 tracked via DeCarbonIQ SSOT. Annual disclosure recommended.","72"],
            ["Energy Transition Plan","Pipeline","Tower solarisation programme and SLL KPIs constitute transition plan framework.","55"],
            ["ESG Governance","Partial","ESG committee active. TCFD alignment in progress. Chief Sustainability Officer role.","60"],
            ["SDG Alignment","Active","9 SDGs tracked and mapped to business activities and capital programmes.","65"],
          ].map(([area,status,note,score])=>(
            <div key={area} style={{marginBottom:10,borderBottom:"1px solid #0f172a",paddingBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                <p style={{fontSize:10,fontWeight:700,color:"#f1f5f9"}}>{area}</p>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:3,background:status==="Active"?"#14532d20":status==="Partial"?"#78350f20":"#1e293b",color:status==="Active"?"#22c55e":status==="Partial"?"#f59e0b":"#64748b"}}>{status}</span>
                  <span style={{fontSize:10,fontWeight:900,color:Number(score)>=65?"#22c55e":"#f59e0b"}}>{score}/100</span>
                </div>
              </div>
              <p style={{fontSize:9,color:"#64748b"}}>{note}</p>
            </div>
          ))}
        </div>
      )}

      {/* Reports */}
      {activeTab==="reports" && (
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}} data-testid="esg-reports">
          {[["ifrs-s2","IFRS S2 Report","ISSB 2023"],["executive","Executive Report","Board"],["sdg-impact","SDG Impact Report","UN SDGs"],
            ["climate-intelligence","Climate Intelligence","GHG Protocol"],["sustainable-finance","Sustainable Finance","ICMA GBP"],["carbon-markets","Carbon Markets","VCM/Art.6"]
          ].map(([k,l,b])=>(
            <button key={k} onClick={()=>downloadPdf(k)} disabled={downloading===k}
              style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6,background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16,cursor:"pointer"}}
              data-testid={`download-${k}`}>
              {downloading===k?<Loader size={16} className="animate-spin" color="#FFCB00"/>:<Download size={16} color="#FFCB00"/>}
              <span style={{fontSize:10,fontWeight:700,color:"#f1f5f9"}}>{l}</span>
              <span style={{fontSize:8,color:"#475569"}}>{b}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
